import { useEffect, useState } from 'react'
import { GitBranch, Network, Pencil, Pin, PinOff, Workflow, X } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import OntologyInspectorEvidence from '@/frontend/features/Fde/OntologyInspectorEvidence'
import OntologyInspectorData from '@/frontend/features/Fde/OntologyInspectorData'
import OntologyInspectorHistory from '@/frontend/features/Fde/OntologyInspectorHistory'
import OntologyInspectorOverview from '@/frontend/features/Fde/OntologyInspectorOverview'
import OntologyInspectorRelationships from '@/frontend/features/Fde/OntologyInspectorRelationships'
import FdeRecordStatus from '@/frontend/features/Fde/FdeRecordStatus'
import type { DataAsset, DataBinding, Evidence, FdeCopy, ModelElement, OntologyChange, ProcessMap, Relationship, SelectedProcess } from '@/frontend/features/Fde/types'

export interface OntologyInspectorProps {
  projectId: string
  selectedNode?: ModelElement
  selectedRelationship?: Relationship
  processMap: ProcessMap | null
  selectedProcess: SelectedProcess | null
  dataAssets: DataAsset[]
  dataBindings: DataBinding[]
  evidences: Evidence[]
  elements: ModelElement[]
  relationships: Relationship[]
  nodeRelationships: Relationship[]
  manuallyPinned: boolean
  pinLimitReached: boolean
  copy: FdeCopy
  canManage: boolean
  history: OntologyChange[]
  historyLoading: boolean
  historyFailed: boolean
  onEdit: (relationship: Relationship) => void
  onEditElement: (element: ModelElement) => void
  onOpenType: (classId: string) => void
  onOpenProcess: () => void
  onSelectRelationship: (id: string) => void
  onSelectNode: (id: string) => void
  onTogglePin: (id: string) => void
  onClear: () => void
}

export default function OntologyGraphInspector(props: OntologyInspectorProps) {
  const [tab, setTab] = useState('overview')
  const recordId = String(props.selectedNode?.id || props.selectedRelationship?.id || '')
  const isRelationship = Boolean(props.selectedRelationship)
  const evidenceCount = props.selectedNode?.evidences.length || props.selectedRelationship?.evidences.length || 0
  const dataBindingCount = props.dataBindings.filter((binding) => props.selectedNode
    ? String(binding.ontology_class?.id) === String(props.selectedNode.ontology_class?.id)
    : String(binding.link_type?.id) === String(props.selectedRelationship?.link_type.id)).length

  useEffect(() => setTab('overview'), [isRelationship, recordId])
  if (!props.selectedNode && !props.selectedRelationship) return null

  const title = props.selectedNode?.name || `${props.selectedRelationship!.from.name} → ${props.selectedRelationship!.to.name}`
  const status = props.selectedNode?.status || props.selectedRelationship!.status
  const typeLabel = props.selectedNode?.ontology_class?.name || props.selectedNode?.kind || props.copy.workspace.inspector_relationship

  return <>
    <button type="button" className="absolute inset-0 z-10 cursor-default bg-foreground/[0.04] backdrop-blur-[1px]" onClick={props.onClear} aria-label={props.copy.workspace.graph_clear_selection} />
    <aside role="dialog" aria-modal="true" aria-label={title} data-testid="ontology-inspector" className="absolute inset-y-0 right-0 z-20 flex w-[min(44rem,94%)] animate-in flex-col border-l border-border bg-background shadow-2xl duration-200 slide-in-from-right-4">
      <InspectorHeader {...props} title={title} status={status} typeLabel={typeLabel} isRelationship={isRelationship} />
      <Tabs value={tab} onValueChange={setTab} className="min-h-0 flex-1 gap-0">
        <TabsList className="h-11 w-full shrink-0 justify-start rounded-none border-b border-border bg-background px-4 py-0">
          <InspectorTab value="overview" label={props.copy.workspace.inspector_overview} />
          <InspectorTab value="relationships" label={props.copy.workspace.inspector_connections} count={isRelationship ? 2 : props.nodeRelationships.length} />
          <InspectorTab value="data" label={props.copy.workspace.inspector_data} count={dataBindingCount} />
          <InspectorTab value="evidences" label={props.copy.workspace.inspector_evidences} count={evidenceCount} />
          <InspectorTab value="history" label={props.copy.workspace.inspector_history} count={props.history.length} />
        </TabsList>
        <ScrollArea className="min-h-0 min-w-0 flex-1 [&_[data-slot=scroll-area-viewport]>div]:!block [&_[data-slot=scroll-area-viewport]>div]:!min-w-0">
          <TabsContent value="overview" className="m-0 p-5"><OntologyInspectorOverview {...props} /></TabsContent>
          <TabsContent value="relationships" className="m-0 p-4"><OntologyInspectorRelationships {...props} /></TabsContent>
          <TabsContent value="data" className="m-0 p-5"><OntologyInspectorData {...props} /></TabsContent>
          <TabsContent value="evidences" className="m-0 p-5"><OntologyInspectorEvidence evidences={props.selectedNode?.evidences || props.selectedRelationship?.evidences || []} copy={props.copy} /></TabsContent>
          <TabsContent value="history" className="m-0 p-5"><OntologyInspectorHistory history={props.history} loading={props.historyLoading} failed={props.historyFailed} copy={props.copy} /></TabsContent>
        </ScrollArea>
      </Tabs>
    </aside>
  </>
}

function InspectorHeader(props: OntologyInspectorProps & { title: string; status: 'proposto' | 'confirmado'; typeLabel: string; isRelationship: boolean }) {
  return <header className="flex shrink-0 items-start gap-3 border-b border-border px-5 py-4">
    <span className="mt-0.5 flex size-9 shrink-0 items-center justify-center rounded-lg border border-border bg-muted/50 text-primary">{props.isRelationship ? <GitBranch className="size-4" /> : <Network className="size-4" />}</span>
    <div className="min-w-0 flex-1">
      <div className="flex flex-wrap items-center gap-1.5">
        {props.selectedNode?.ontology_class ? <button type="button" onClick={() => props.onOpenType(String(props.selectedNode!.ontology_class!.id))} aria-label={`${props.copy.workspace.open_type}: ${props.typeLabel}`} className="inline-flex h-5 items-center rounded-md border border-border px-2 text-[10px] font-medium hover:border-primary hover:text-primary">{props.typeLabel}</button> : <Badge variant="outline" className="text-[10px]">{props.typeLabel}</Badge>}
        <FdeRecordStatus status={props.status} proposedLabel={props.copy.workspace.proposed} confirmedLabel={props.copy.workspace.confirmed_status} showConfirmed showLabel />
      </div>
      <h2 className="mt-2 text-lg font-semibold leading-tight text-foreground">{props.title}</h2>
      {props.selectedRelationship && <p className="mt-1 text-xs font-semibold uppercase tracking-wide text-primary">{props.selectedRelationship.verb}</p>}
    </div>
    <div className="flex shrink-0 items-center gap-1">
      {props.processMap && <Button type="button" size="sm" variant="outline" onClick={props.onOpenProcess}><Workflow />{props.copy.workspace.process_open}</Button>}
      {props.canManage && <Button type="button" size="icon-sm" variant="ghost" onClick={() => props.selectedNode ? props.onEditElement(props.selectedNode) : props.onEdit(props.selectedRelationship!)} aria-label={props.copy.common.edit}><Pencil /></Button>}
      {props.selectedNode && <Button type="button" size="icon-sm" variant="ghost" disabled={!props.manuallyPinned && props.pinLimitReached} onClick={() => props.onTogglePin(String(props.selectedNode!.id))} title={!props.manuallyPinned && props.pinLimitReached ? props.copy.workspace.graph_focus_limit : props.copy.workspace.graph_pin}>{props.manuallyPinned ? <PinOff /> : <Pin />}</Button>}
      <Button type="button" size="icon-sm" variant="ghost" onClick={props.onClear} aria-label={props.copy.workspace.graph_clear_selection}><X /></Button>
    </div>
  </header>
}

function InspectorTab({ value, label, count }: { value: string; label: string; count?: number }) {
  return <TabsTrigger value={value} aria-label={count === undefined ? label : `${label} ${count}`} className="h-full rounded-none border-b-2 border-transparent px-3 text-xs shadow-none data-[state=active]:border-primary data-[state=active]:shadow-none">{label}{count !== undefined && <span className="rounded-full bg-muted px-1.5 py-0.5 text-[9px] tabular-nums text-muted-foreground">{count}</span>}</TabsTrigger>
}
